import React from "react";
import {
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Table,
    Badge,
    Button,
    Input,
    InputGroup,
    Form,
    Spinner
} from "reactstrap";
import { useNavigate, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(amount, currency) {
    const value = amount / 100;
    try {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: currency.toUpperCase()
        }).format(value);
    } catch {
        return `${currency.toUpperCase()} ${value.toFixed(2)}`;
    }
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    } catch {
        return dateStr;
    }
}

function formatDateLong(dateStr) {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    } catch {
        return dateStr;
    }
}

/** Returns whole days until the given date (negative = overdue, null = no date) */
function daysUntil(dateStr) {
    if (!dateStr) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const target = new Date(dateStr);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

function BoolBadge({ value }) {
    return value
        ? <Badge color="success">Yes</Badge>
        : <Badge color="secondary">No</Badge>;
}

function SubscriptionStatusBadge({ status }) {
    const colours = {
        active:   'success',
        trialing: 'info',
        past_due: 'warning',
        canceled: 'danger',
        unpaid:   'danger'
    };
    return (
        <Badge color={colours[status] || 'secondary'}>
            {status || 'none'}
        </Badge>
    );
}

/** Row background based on renewal urgency */
function renewalRowStyle(user, isSelected) {
    if (isSelected) return { cursor: 'pointer', backgroundColor: '#eef2ff' };
    const d = daysUntil(user.nextBillingDate);
    if (d !== null && d <= 7)  return { cursor: 'pointer', backgroundColor: '#fff0f0' };
    if (d !== null && d <= 30) return { cursor: 'pointer', backgroundColor: '#fffbeb' };
    return { cursor: 'pointer' };
}

// ── Sortable column header ─────────────────────────────────────────────────────

function SortTh({ label, sortKey, currentKey, currentDir, onSort, className }) {
    const active = currentKey === sortKey;
    return (
        <th
            className={className}
            onClick={() => onSort(sortKey)}
            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        >
            {label}
            <span className="ml-1" style={{ opacity: active ? 1 : 0.3, fontSize: '0.75em' }}>
                {active ? (currentDir === 'asc' ? '▲' : '▼') : '⇅'}
            </span>
        </th>
    );
}

// ── Filter definitions ─────────────────────────────────────────────────────────

const FILTER_DEFS = [
    { label: 'Verified',  key: 'verified',            opts: [['all','All'],['yes','Yes'],['no','No']] },
    { label: 'Complete',  key: 'registrationComplete', opts: [['all','All'],['yes','Yes'],['no','No']] },
    { label: 'Locked',    key: 'isLocked',             opts: [['all','All'],['yes','Yes'],['no','No']] },
    { label: 'Account',   key: 'accountClosed',        opts: [['all','All'],['open','Open'],['closed','Closed']] },
    { label: 'Admin',     key: 'isAdmin',              opts: [['all','All'],['yes','Yes'],['no','No']] },
    {
        label: 'Renewing',
        key: 'renewing',
        opts: [['all','Any'],['7','Within 7 days'],['14','Within 14 days'],['30','Within 30 days'],['none','No subscription']]
    },
];

const DEFAULT_FILTERS = {
    verified: 'all',
    registrationComplete: 'all',
    isLocked: 'all',
    accountClosed: 'all',
    isAdmin: 'all',
    renewing: 'all'
};

// ── Component ──────────────────────────────────────────────────────────────────

class AdminMembers extends React.Component {
    state = {
        isLoading: false,
        allUsers: [],
        searchQuery: "",
        sortKey: 'nextBillingDate',
        sortDir: 'asc',
        filters: { ...DEFAULT_FILTERS },
        selectedUserId: null,
        membershipDetail: null,
        membershipLoading: false,
        unauthorized: false
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (parsed?.user?.isAdmin) {
                this.loadUsers();
            } else {
                this.setState({ unauthorized: true });
            }
        } catch (e) {
            console.error("AdminMembers componentDidMount error:", e);
            this.setState({ unauthorized: true });
        }
    }

    loadUsers = () => {
        this.setState({ isLoading: true });
        Api.get("/user/admin/all")
            .then(({ data }) => this.setState({ allUsers: data || [], isLoading: false }))
            .catch(err => {
                console.error("Error fetching users:", err);
                this.setState({ isLoading: false });
            });
    };

    // ── Sort ──────────────────────────────────────────────────────────────────

    handleSort = (key) => {
        this.setState(prev => ({
            sortKey: key,
            sortDir: prev.sortKey === key && prev.sortDir === 'asc' ? 'desc' : 'asc',
            selectedUserId: null,
            membershipDetail: null
        }));
    };

    // ── Search ────────────────────────────────────────────────────────────────

    handleSearchChange = (e) => this.setState({ searchQuery: e.target.value });

    handleSearchSubmit = (e) => {
        e.preventDefault();
        this.setState({ selectedUserId: null, membershipDetail: null });
    };

    handleClearSearch = () => {
        this.setState({ searchQuery: "", selectedUserId: null, membershipDetail: null });
    };

    // ── Filters ───────────────────────────────────────────────────────────────

    handleFilterChange = (key, value) => {
        this.setState(prev => ({
            filters: { ...prev.filters, [key]: value },
            selectedUserId: null,
            membershipDetail: null
        }));
    };

    resetFilters = () => {
        this.setState({ filters: { ...DEFAULT_FILTERS }, selectedUserId: null, membershipDetail: null });
    };

    // ── Compute displayed rows ────────────────────────────────────────────────

    getDisplayedUsers() {
        const { allUsers, searchQuery, sortKey, sortDir, filters } = this.state;
        const q = searchQuery.trim().toLowerCase();

        let result = allUsers.filter(u => {
            if (q && !(
                `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
                u.email.toLowerCase().includes(q)
            )) return false;

            if (filters.verified !== 'all' &&
                (filters.verified === 'yes') !== !!u.verified) return false;
            if (filters.registrationComplete !== 'all' &&
                (filters.registrationComplete === 'yes') !== !!u.registrationComplete) return false;
            if (filters.isLocked !== 'all' &&
                (filters.isLocked === 'yes') !== !!u.isLocked) return false;
            if (filters.isAdmin !== 'all' &&
                (filters.isAdmin === 'yes') !== !!u.isAdmin) return false;
            if (filters.accountClosed !== 'all' &&
                (filters.accountClosed === 'closed') !== !!u.accountClosed) return false;

            if (filters.renewing !== 'all') {
                if (filters.renewing === 'none') {
                    if (u.nextBillingDate) return false;
                } else {
                    const d = daysUntil(u.nextBillingDate);
                    if (d === null || d > parseInt(filters.renewing)) return false;
                }
            }

            return true;
        });

        result.sort((a, b) => {
            let aVal, bVal;
            switch (sortKey) {
                case 'name':
                    aVal = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
                    bVal = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
                    break;
                case 'email':
                    aVal = (a.email || '').toLowerCase();
                    bVal = (b.email || '').toLowerCase();
                    break;
                case 'verified':
                    aVal = a.verified ? 1 : 0; bVal = b.verified ? 1 : 0; break;
                case 'registrationComplete':
                    aVal = a.registrationComplete ? 1 : 0; bVal = b.registrationComplete ? 1 : 0; break;
                case 'isLocked':
                    aVal = a.isLocked ? 1 : 0; bVal = b.isLocked ? 1 : 0; break;
                case 'accountClosed':
                    aVal = a.accountClosed ? new Date(a.accountClosed).getTime() : 0;
                    bVal = b.accountClosed ? new Date(b.accountClosed).getTime() : 0;
                    break;
                case 'isAdmin':
                    aVal = a.isAdmin ? 1 : 0; bVal = b.isAdmin ? 1 : 0; break;
                case 'nextBillingDate':
                    // nulls sort to end regardless of direction
                    aVal = a.nextBillingDate ? new Date(a.nextBillingDate).getTime() : Infinity;
                    bVal = b.nextBillingDate ? new Date(b.nextBillingDate).getTime() : Infinity;
                    break;
                default:
                    return 0;
            }
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }

    // ── Row expansion ──────────────────────────────────────────────────────────

    handleRowClick = (user) => {
        if (this.state.selectedUserId === user.id) {
            this.setState({ selectedUserId: null, membershipDetail: null });
            return;
        }
        this.setState({ selectedUserId: user.id, membershipDetail: null, membershipLoading: true });
        Api.get(`/user/${user.id}/membership`)
            .then(({ data }) => this.setState({ membershipDetail: data, membershipLoading: false }))
            .catch(err => {
                console.error("Error fetching membership:", err);
                this.setState({ membershipLoading: false });
            });
    };

    renderMembershipDetail() {
        const { membershipDetail, membershipLoading } = this.state;

        if (membershipLoading) {
            return (
                <tr>
                    <td colSpan={9} className="text-center py-3">
                        <Spinner size="sm" color="primary" /> Loading membership…
                    </td>
                </tr>
            );
        }

        if (!membershipDetail) return null;

        const { user, hasActiveSubscription, subscriptions } = membershipDetail;

        return (
            <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td colSpan={9} className="px-4 py-3">
                    <Row>
                        <Col md="4">
                            <p className="mb-1 small text-muted text-uppercase font-weight-bold">Account</p>
                            <p className="mb-1"><strong>{user.firstName} {user.lastName}</strong></p>
                            <p className="mb-1 small">{user.email}</p>
                            {user.externalCustomerId && (
                                <p className="mb-0 small text-muted">
                                    Stripe: <code>{user.externalCustomerId}</code>
                                </p>
                            )}
                            <p className="mb-0 small text-muted">
                                Joined: {formatDateLong(user.createdAt)}
                            </p>
                        </Col>

                        <Col md="4">
                            <p className="mb-1 small text-muted text-uppercase font-weight-bold">Subscription</p>
                            {hasActiveSubscription && subscriptions?.length > 0 ? (
                                subscriptions.map(sub => (
                                    <div key={sub.id} className="mb-2">
                                        <div className="mb-1">
                                            <SubscriptionStatusBadge status={sub.status} />
                                        </div>
                                        <p className="mb-0 small">
                                            <strong>{formatCurrency(sub.amount, sub.currency)}</strong>
                                            {' '}/ {sub.currency?.toUpperCase()}
                                        </p>
                                        <p className="mb-0 small text-muted">
                                            Next billing: {formatDateLong(sub.next_billing_date)}
                                        </p>
                                        <p className="mb-0 small text-muted">
                                            ID: <code>{sub.id}</code>
                                        </p>
                                    </div>
                                ))
                            ) : (
                                <p className="mb-0 small text-muted">No active subscription</p>
                            )}
                        </Col>

                        <Col md="4">
                            <p className="mb-1 small text-muted text-uppercase font-weight-bold">Status flags</p>
                            <table className="small">
                                <tbody>
                                    <tr><td className="pr-3 text-muted">Verified</td><td><BoolBadge value={user.verified} /></td></tr>
                                    <tr><td className="pr-3 text-muted">Registration complete</td><td><BoolBadge value={user.registrationComplete} /></td></tr>
                                    <tr><td className="pr-3 text-muted">Admin</td><td><BoolBadge value={user.isAdmin} /></td></tr>
                                    <tr><td className="pr-3 text-muted">Locked</td><td><BoolBadge value={user.isLocked} /></td></tr>
                                    {user.accountClosed && (
                                        <tr>
                                            <td className="pr-3 text-muted">Account closed</td>
                                            <td><Badge color="danger">{formatDateLong(user.accountClosed)}</Badge></td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Col>
                    </Row>
                </td>
            </tr>
        );
    }

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;

        const { isLoading, searchQuery, sortKey, sortDir, filters, selectedUserId, allUsers } = this.state;
        const displayedUsers = this.getDisplayedUsers();
        const hasActiveFilters = Object.values(filters).some(v => v !== 'all');
        const thProps = { currentKey: sortKey, currentDir: sortDir, onSort: this.handleSort };

        // Summary counts for the legend
        const renewingIn7  = allUsers.filter(u => { const d = daysUntil(u.nextBillingDate); return d !== null && d <= 7; }).length;
        const renewingIn30 = allUsers.filter(u => { const d = daysUntil(u.nextBillingDate); return d !== null && d <= 30; }).length;

        return (
            <>
                <SeoNoIndex noindex={true} />

                <div className="position-relative">
                    <section className="section section-lg section-shaped pb-250">
                        <div className="shape shape-style-1 shape-default">
                            <span /><span /><span /><span /><span /><span /><span /><span /><span />
                        </div>
                        <Container className="py-lg-md d-flex">
                            <div className="col px-0">
                                <Row>
                                    <Col lg="8">
                                        <h1 className="display-3 text-white">
                                            Building your song-flows<br /><span>manage members</span>
                                        </h1>
                                    </Col>
                                </Row>
                            </div>
                        </Container>
                        <div className="separator separator-bottom separator-skew">
                            <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                            </svg>
                        </div>
                    </section>

                    <section className="section section-lg pt-lg-0 mt--200">
                        <Container>
                            <Row className="justify-content-center">
                                <Col lg="12">
                                    <Row className="row-grid">
                                        <Col lg="12">
                                            <Card className="shadow border-0">
                                                <CardBody className="py-5">

                                                    <Button color="link" onClick={() => this.props.navigate("/admin")} className="mb-3 p-0">
                                                        &larr; Back to menu
                                                    </Button>

                                                    {/* Renewal alert summary */}
                                                    {!isLoading && renewingIn7 > 0 && (
                                                        <div className="alert mb-3" style={{ backgroundColor: '#fff0f0', borderLeft: '4px solid #dc3545' }}>
                                                            <strong>⚠ {renewingIn7} member{renewingIn7 !== 1 ? 's' : ''} renew{renewingIn7 === 1 ? 's' : ''} within 7 days</strong>
                                                            {renewingIn30 > renewingIn7 && (
                                                                <span className="text-muted ml-2">
                                                                    ({renewingIn30} within 30 days)
                                                                </span>
                                                            )}
                                                            <Button
                                                                color="link"
                                                                size="sm"
                                                                className="ml-3 p-0"
                                                                onClick={() => this.handleFilterChange('renewing', '7')}
                                                            >
                                                                Show only these members
                                                            </Button>
                                                        </div>
                                                    )}

                                                    {/* Search */}
                                                    <Form inline onSubmit={this.handleSearchSubmit} className="mb-3">
                                                        <InputGroup>
                                                            <Input
                                                                type="text"
                                                                placeholder="Search by name or email…"
                                                                value={searchQuery}
                                                                onChange={this.handleSearchChange}
                                                            />
                                                            <Button color="primary" type="submit">Search</Button>
                                                            <Button
                                                                color="secondary"
                                                                onClick={this.handleClearSearch}
                                                                disabled={!searchQuery.trim()}
                                                                className="ml-2"
                                                            >
                                                                Clear
                                                            </Button>
                                                        </InputGroup>
                                                    </Form>

                                                    {/* Filters */}
                                                    <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: '10px' }}>
                                                        <small className="text-muted font-weight-bold">Filter:</small>
                                                        {FILTER_DEFS.map(({ label, key, opts }) => (
                                                            <div key={key} className="d-flex align-items-center" style={{ gap: '4px' }}>
                                                                <small className="text-muted">{label}:</small>
                                                                <Input
                                                                    type="select"
                                                                    bsSize="sm"
                                                                    value={filters[key]}
                                                                    onChange={e => this.handleFilterChange(key, e.target.value)}
                                                                    style={{ width: 'auto' }}
                                                                >
                                                                    {opts.map(([val, lbl]) => (
                                                                        <option key={val} value={val}>{lbl}</option>
                                                                    ))}
                                                                </Input>
                                                            </div>
                                                        ))}
                                                        {hasActiveFilters && (
                                                            <Button size="sm" color="link" className="p-0 text-danger" onClick={this.resetFilters}>
                                                                Reset filters
                                                            </Button>
                                                        )}
                                                    </div>

                                                    {/* Legend */}
                                                    <div className="d-flex align-items-center mb-3" style={{ gap: '12px' }}>
                                                        <small className="text-muted">
                                                            Showing {displayedUsers.length} of {allUsers.length} members
                                                        </small>
                                                        <small style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#fff0f0', border: '1px solid #dc3545', borderRadius: 2 }} />
                                                            Renewing ≤ 7 days
                                                        </small>
                                                        <small style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            <span style={{ display: 'inline-block', width: 12, height: 12, backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 2 }} />
                                                            Renewing ≤ 30 days
                                                        </small>
                                                    </div>

                                                    {isLoading ? (
                                                        <div className="text-center py-4">
                                                            <Spinner color="primary" />
                                                        </div>
                                                    ) : (
                                                        <div className="table-responsive">
                                                            <Table hover size="sm" className="align-middle mb-0">
                                                                <thead className="thead-light">
                                                                    <tr>
                                                                        <SortTh label="Name"        sortKey="name"                 {...thProps} />
                                                                        <SortTh label="Email"       sortKey="email"                {...thProps} />
                                                                        <SortTh label="Verified"    sortKey="verified"             {...thProps} className="text-center" />
                                                                        <SortTh label="Complete"    sortKey="registrationComplete" {...thProps} className="text-center" />
                                                                        <SortTh label="Locked"      sortKey="isLocked"             {...thProps} className="text-center" />
                                                                        <SortTh label="Closed"      sortKey="accountClosed"        {...thProps} className="text-center" />
                                                                        <SortTh label="Admin"       sortKey="isAdmin"              {...thProps} className="text-center" />
                                                                        <SortTh label="Next Renewal" sortKey="nextBillingDate"     {...thProps} className="text-center" />
                                                                        <th></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {displayedUsers.length > 0 ? displayedUsers.map(user => {
                                                                        const isClosed   = !!user.accountClosed;
                                                                        const isSelected = selectedUserId === user.id;
                                                                        const d          = daysUntil(user.nextBillingDate);

                                                                        return (
                                                                            <React.Fragment key={user.id}>
                                                                                <tr
                                                                                    onClick={() => this.handleRowClick(user)}
                                                                                    style={{
                                                                                        ...renewalRowStyle(user, isSelected),
                                                                                        opacity: isClosed ? 0.45 : 1
                                                                                    }}
                                                                                >
                                                                                    <td>
                                                                                        {user.firstName} {user.lastName}
                                                                                        {isClosed && (
                                                                                            <Badge color="danger" className="ml-2" style={{ fontSize: '0.7em' }}>closed</Badge>
                                                                                        )}
                                                                                    </td>
                                                                                    <td>{user.email}</td>
                                                                                    <td className="text-center"><BoolBadge value={user.verified} /></td>
                                                                                    <td className="text-center"><BoolBadge value={user.registrationComplete} /></td>
                                                                                    <td className="text-center">
                                                                                        {user.isLocked ? <Badge color="warning">Yes</Badge> : <Badge color="secondary">No</Badge>}
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        {isClosed
                                                                                            ? <Badge color="danger">{formatDate(user.accountClosed)}</Badge>
                                                                                            : <span className="text-muted">—</span>}
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        {user.isAdmin ? <Badge color="primary">Yes</Badge> : <Badge color="secondary">No</Badge>}
                                                                                    </td>
                                                                                    <td className="text-center" style={{ whiteSpace: 'nowrap' }}>
                                                                                        {user.nextBillingDate ? (
                                                                                            <>
                                                                                                <SubscriptionStatusBadge status={user.subscriptionStatus} />
                                                                                                <div className="small mt-1">
                                                                                                    {formatDate(user.nextBillingDate)}
                                                                                                    {d !== null && d <= 30 && (
                                                                                                        <Badge
                                                                                                            color={d <= 7 ? 'danger' : 'warning'}
                                                                                                            className="ml-1"
                                                                                                        >
                                                                                                            {d <= 0 ? 'today' : `${d}d`}
                                                                                                        </Badge>
                                                                                                    )}
                                                                                                </div>
                                                                                            </>
                                                                                        ) : (
                                                                                            <span className="text-muted">—</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td className="text-muted small text-right">
                                                                                        {isSelected ? '▲' : '▼'}
                                                                                    </td>
                                                                                </tr>

                                                                                {isSelected && this.renderMembershipDetail()}
                                                                            </React.Fragment>
                                                                        );
                                                                    }) : (
                                                                        <tr>
                                                                            <td colSpan={9} className="text-center text-muted py-4">
                                                                                No members match the current search and filters.
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    )}

                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Container>
                    </section>
                </div>
            </>
        );
    }
}

export default withNavigateHook(AdminMembers);

function withNavigateHook(Component) {
    return props => {
        const navigate = useNavigate();
        return <Component {...props} navigate={navigate} />;
    };
}
